/**
 * QUOTATIONS — WHAT WE OFFERED, AND WHAT THEY SAID.
 *
 * An issued offer is a piece of paper the customer holds. So it is never
 * edited: it is REVISED, which makes a new revision with its own number and
 * leaves the old one readable. The screen shows the whole lineage, because the
 * question that actually gets asked six months later is "what did we quote him
 * in June", and the answer has to survive somebody quoting him again in July.
 *
 * The decision is recorded as a fact with a date — accepted or declined, on
 * such-and-such a day — and an acceptance creates the order at the prices that
 * were offered, not at today's.
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { api, can, mayDecideOwn, type Session, type Views } from '../api.js';
import { useAsk, useRun, go, routeQuery } from '../hooks.js';
import { Banner, Button, Card, Kpi, Loading, Money, Pill, Register, Section, Select, Text, TextArea, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { CustomerPickerById } from '../pickers.js';
import { count, date, money, today, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';
import { LineEditor, emptyLine, firstLine, toLineBodies, type LineRow, rupeesToPaiseOrNull } from '../lines.js';
import type { SkuRow } from '../../../src/views/masters.js';

const MESSAGES = screenById('quotes')!.messages;

// 0175 — CTS1's four printed schedules, in the order they print.
const SCHEDULE_KINDS = ['security', 'additional', 'service', 'damage'] as const;
const SCHEDULE_TITLES: Record<string, string> = {
  security: 'Cylinder security deposit',
  additional: 'Additional charges — accessories and parts',
  service: 'Service charges',
  damage: 'Cylinder damage charges',
};

export function Quotes({ id, session }: { id: string | null; session: Session }) {
  if (id === 'new') return <NewQuote />;
  if (id) return <OneQuote quotationId={Number(id)} session={session} />;
  return <QuoteRegister session={session} />;
}

const STATUS_WORDS: Record<string, { label: string; tone: 'empty' | 'primary' | 'good' | 'danger' | 'money' | 'muted' | 'testing' }> = {
  draft: { label: 'Draft', tone: 'muted' }, pending_approval: { label: 'Awaiting approval', tone: 'money' }, approved: { label: 'Approved', tone: 'testing' },
  issued: { label: 'Sent', tone: 'primary' }, accepted: { label: 'Won', tone: 'good' }, declined: { label: 'Lost', tone: 'danger' },
  superseded: { label: 'Superseded', tone: 'muted' }, withdrawn: { label: 'Withdrawn', tone: 'muted' },
};
export function statusPill(status: string, expired: boolean) {
  const w = STATUS_WORDS[status] ?? { label: words(status), tone: 'muted' as const };
  return <>{expired ? <Pill tone="danger">EXPIRED</Pill> : <Pill tone={w.tone}>{w.label}</Pill>}</>;
}

/** 11 Oct — CTS1's quotation list: the pipeline strip, the status chips, the register. */
function QuoteRegister({ session }: { session: Session }) {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [days, setDays] = useState<'30' | '365' | ''>('');
  const stats = useAsk(() => api.view('quotes.stats', { ...(days ? { days: Number(days) } : {}) }), [days], MESSAGES);
  const asked = useAsk(() => api.view('quotes.list', { ...(status ? { status } : {}), ...(q.trim() ? { q: q.trim() } : {}), page, pageSize: 50 }), [status, q, page], MESSAGES);
  type Row = Views['quotes.list']['result']['rows'][number];
  const columns: readonly Column<Row>[] = [
    { key: 'number', head: 'Quotation', cell: (r) => <>{r.number ? <strong>{r.number}</strong> : <span className="muted">draft #{r.quotationId}</span>}{r.revisionNo > 1 ? <div className="muted">rev {r.revisionNo}</div> : null}</> },
    { key: 'customer', head: 'Customer', cell: (r) => <><strong>{r.customerName}</strong>{r.customerReference ? <div className="muted">their ref {r.customerReference}</div> : null}</> },
    { key: 'date', head: 'Date', cell: (r) => date(r.issuedOn ?? r.createdAt) },
    { key: 'valid', head: 'Valid until', cell: (r) => r.validUntil ? <span style={{ color: r.expired ? 'var(--danger, #b91c1c)' : undefined }}>{date(r.validUntil)}</span> : <span className="muted">—</span> },
    { key: 'total', head: 'Value', right: true, cell: (r) => <Money paise={r.totalPaise} /> },
    { key: 'status', head: 'Status', cell: (r) => <>{statusPill(r.status, r.expired)}{r.orderId ? <div className="muted">order #{r.orderId}</div> : null}</> },
    { key: 'by', head: 'By', cell: (r) => <>{r.createdByName ?? '—'}{r.approvedByName ? <div className="muted">approved by {r.approvedByName}</div> : null}</> },
  ];
  const st = stats.data;
  const chips: { value: string; label: string; n?: number | undefined }[] = [
    { value: '', label: 'All', n: st?.raised }, { value: 'draft', label: 'Draft', n: st?.drafts }, { value: 'pending_approval', label: 'Awaiting approval', n: st?.awaitingApproval },
    { value: 'approved', label: 'Approved', n: st?.approved }, { value: 'issued', label: 'Sent', n: st?.sent }, { value: 'accepted', label: 'Won', n: st?.won }, { value: 'declined', label: 'Lost', n: st?.lost },
  ];
  return (
    <Page title="Techno-Commercial Offers" sub="What we offered, what it came to, and what the customer said. An approved offer governs the customer's rates; a sent one is the paper they hold."
          actions={can(session, 'quote.draft') ? <Button kind="primary" onClick={() => go('#/quotes/new')}>+ New quotation</Button> : null}>
      <div className="kpis" data-quote-stats>
        <Kpi label="Quotes raised" value={count(st?.raised ?? 0)} note={st ? `${money(st.valueRaisedPaise)} quoted` : ''} />
        <Kpi label="Awaiting approval" value={count(st?.awaitingApproval ?? 0)} tone={(st?.awaitingApproval ?? 0) > 0 ? 'money' : 'empty'} href="#/approvals" />
        <Kpi label="Sent, waiting" value={count(st?.sent ?? 0)} tone="primary" note={st && st.waitingOver3Days > 0 ? `${count(st.waitingOver3Days)} over 3 days` : 'with the customer'} />
        <Kpi label="Won" value={count(st?.won ?? 0)} tone="good" note={st ? money(st.wonPaise) : ''} />
        <Kpi label="Lost" value={count(st?.lost ?? 0)} tone={(st?.lost ?? 0) > 0 ? 'danger' : 'empty'} />
        <Kpi label="Win rate" value={st?.winRatePct == null ? '—' : `${st.winRatePct}%`} note="won ÷ decided" />
        {st && st.expiredOpen > 0 ? <Kpi label="Expired, still open" value={count(st.expiredOpen)} tone="danger" note="revise or withdraw" /> : null}
      </div>
      <div className="toolbar" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="seg" role="tablist">
          {chips.map((c) => <button key={c.value} type="button" className={`seg-btn${status === c.value ? ' on' : ''}`} onClick={() => { setStatus(c.value); setPage(1); }}>{c.label}{c.n !== undefined ? ` ${count(c.n)}` : ''}</button>)}
        </div>
        <Select name="range" label="" value={days} onChange={(v) => setDays(v as typeof days)} options={[{ value: '', label: 'All time' }, { value: '30', label: 'Last 30 days' }, { value: '365', label: 'Last year' }]} />
        <Text name="quote-q" label="" value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search number or reference…" />
      </div>
      <Card>
        {asked.loading && !asked.data ? <Loading what="quotations" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : <><Register columns={columns} rows={asked.data!.rows} empty="No quotation matches that." onOpen={(r) => go(`#/quotes/${r.quotationId}`)}
 />
              <Pager page={asked.data!.page} onPage={setPage} /></>}
      </Card>
    </Page>
  );
}

function OneQuote({ quotationId, session }: { quotationId: number; session: Session }) {
  const asked = useAsk(() => api.view('quotes.get', { quotationId }), [quotationId], MESSAGES);
  const skus = useAsk(() => api.view('skus.list', { active: true, pageSize: 200 }), [], MESSAGES);
  const gases = useAsk(() => api.view('gases.list', {}), [], MESSAGES);
  const runner = useRun(MESSAGES);
  const [deciding, setDeciding] = useState(false);
  const [revising, setRevising] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState<'approved' | 'rejected' | null>(null);
  const [approveReason, setApproveReason] = useState('');

  if (asked.loading) return <Page title="Techno-Commercial Offer"><Loading what="the offer" /></Page>;
  if (asked.error) return <Page title="Techno-Commercial Offer"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Page>;
  const d = asked.data!;
  const q = d.quotation;
  const draft = q.status === 'draft';
  const pending = q.status === 'pending_approval';
  const approved = q.status === 'approved';
  const issued = q.status === 'issued';
  // 12 Oct — raised by me, AND the self-approval rule actually binds me. It
  // does not bind the CEO, nor a user who holds no business role at all.
  const raisedByMe = q.submittedBy === session.actorId || (q.submittedBy === null && q.createdBy === session.actorId);
  const mine = raisedByMe && !mayDecideOwn(session);
  async function submit() { const ok = await runner.run(() => api.command('quote.submit', { quotationId })); if (ok) { setSubmitting(false); asked.reload(); } }
  async function decideApproval() {
    if (!approving) return;
    const ok = await runner.run((retryKey) => api.command('quote.approve', { quotationId, decision: approving, ...(approveReason.trim() ? { reason: approveReason.trim() } : {}) }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setApproving(null); setApproveReason(''); asked.reload(); }
  }
  const waText = `Dear ${q.customerName}, please find our offer ${q.number ?? ''} for ${money(q.totalPaise)}${q.validUntil ? `, valid until ${date(q.validUntil)}` : ''}. Regards.`;

  return (
    <Page title={q.number ? `Techno-Commercial Offer ${q.number}` : `Draft offer #${q.quotationId}`}
          sub={`${q.customerName} · revision ${q.revisionNo}${q.customerReference ? ` · their reference ${q.customerReference}` : ''}${q.issuedOn ? ` · sent ${date(q.issuedOn)}` : ''}${q.validUntil ? ` · valid until ${date(q.validUntil)}` : ''}`}
          actions={<>
            {statusPill(q.status, q.expired)}
            <Button onClick={() => go(`#/customers/${q.customerId}`)}>The customer</Button>
            {draft && can(session, 'quote.draft') ? <Button onClick={() => setEditing((v) => !v)}>{editing ? 'Leave the lines' : 'Edit'}</Button> : null}
            {draft && can(session, 'quote.draft') ? <Button kind="primary" onClick={() => setSubmitting((v) => !v)}>{submitting ? 'Not yet' : 'Submit for approval'}</Button> : null}
            {pending && can(session, 'quote.approve') && !mine ? <><Button kind="primary" onClick={() => setApproving('approved')}>Approve</Button><Button kind="danger" onClick={() => setApproving('rejected')}>Reject</Button></> : null}
            {(draft || approved) && can(session, 'quote.issue') ? <Button kind={approved ? 'primary' : 'ghost'} onClick={() => setIssuing((v) => !v)}>{issuing ? 'Not yet' : 'Issue it'}</Button> : null}
            {issued && can(session, 'quote.decide') ? <Button kind="primary" onClick={() => setDeciding((v) => !v)}>{deciding ? 'Leave it open' : 'Record their answer'}</Button> : null}
            {(issued || approved) && can(session, 'quote.revise') ? <Button onClick={() => setRevising((v) => !v)}>{revising ? 'Keep it as it is' : 'Revise it'}</Button> : null}
            {q.documentId ? <a className="btn" href={`/documents/${q.documentId}.pdf`} target="_blank" rel="noreferrer">Print / PDF</a> : null}
            {issued ? <a className="btn" href={`https://wa.me/?text=${encodeURIComponent(waText)}`} target="_blank" rel="noreferrer">WhatsApp</a> : null}
          </>}>

      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}
      {pending ? <Banner kind="info" title={mine ? 'Awaiting approval — you raised this, so somebody else decides it.' : raisedByMe ? 'Awaiting approval — you raised it, and you may decide it yourself.' : 'Awaiting approval.'} detail="Approving writes the customer’s agreed rates from this offer and makes it the governing quote; rejecting sends it back to draft with the reason." /> : null}
      {q.rejectionReason && draft ? <Banner kind="warn" title="Sent back." detail={q.rejectionReason} /> : null}
      {approved ? <Banner kind="good" title={`Approved${q.approvedByName ? ` by ${q.approvedByName}` : ''}${q.approvedAt ? ` on ${date(q.approvedAt)}` : ''}.`} detail={q.applyToCard ? 'The customer’s card now carries these rates. Issue it to send the Techno-Commercial Offer.' : 'The customer’s card was left alone (this offer does not save to the card). Issue it to send the offer.'} /> : null}
      {issued ? (
        <Banner kind="info" title="This offer has been sent, so it is not edited."
                detail="Revising it makes a new revision with its own number and leaves this one readable — the customer has this paper, and what they were told stays true." />
      ) : null}
      {q.expired && (issued || approved || draft || pending) ? (
        <Banner kind="warn" title={`This offer lapsed on ${date(q.validUntil)}.`} detail="Recording an acceptance or approving it is refused; revise it with a new validity instead." />
      ) : null}

      {submitting ? (
        <Card title="Submit this offer for approval" sub="An approver other than you decides it. On approval its rates become the customer’s agreed card.">
          <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy}>{runner.busy ? 'Submitting…' : 'Submit for approval'}</Button>
        </Card>
      ) : null}
      {approving ? (
        <Card title={approving === 'approved' ? 'Approve this offer' : 'Reject this offer'}
              sub={approving === 'approved' ? 'Its rates become the customer’s agreed card from today, and it becomes their governing quote.' : 'It goes back to draft with your reason, for the person who drafted it.'}>
          <TextArea name="approve-reason" label={approving === 'approved' ? 'Note (optional)' : 'Why'} value={approveReason} onChange={setApproveReason} />
          <div className="toolbar">
            <Button kind={approving === 'approved' ? 'primary' : 'danger'} disabled={runner.busy || (approving === 'rejected' && !approveReason.trim())} onClick={() => { void decideApproval(); }}>{approving === 'approved' ? 'Approve it' : 'Reject it'}</Button>
            <Button onClick={() => setApproving(null)}>Cancel</Button>
          </div>
        </Card>
      ) : null}
      {editing ? <EditLines quotationId={quotationId} lines={d.lines} skus={skus.data?.rows ?? []} onDone={() => { setEditing(false); asked.reload(); }} /> : null}
      {issuing ? <IssueQuote quotationId={quotationId} onDone={() => { setIssuing(false); asked.reload(); }} /> : null}
      {revising ? <ReviseQuote quotationId={quotationId} onDone={() => { setRevising(false); asked.reload(); }} /> : null}
      {deciding ? <DecideQuote quotationId={quotationId} onDone={() => { setDeciding(false); asked.reload(); }} /> : null}

      <Card title="What was offered" sub="the price each line was offered at — an accepted offer becomes an order at these, not at today's">
        <Register
          columns={[
            { key: 'line', head: 'Line', cell: (l: { lineNo: number }) => `#${l.lineNo}` },
            { key: 'gas', head: 'Gas', cell: (l: { gasService: string; waterCapacityL: number | null }) => <>{words(l.gasService)}{l.waterCapacityL ? <span className="muted"> · {l.waterCapacityL} L</span> : null}</> },
            // F04 — what was asked, in its unit; the cylinders it takes beside it
            // A line quoted in gas has no vessel count yet (0163). It says so
            // rather than printing "0 cylinders", which reads as nothing sold.
            { key: 'qty', head: 'Quantity', right: true, cell: (l: { qty: number | null; qtyRequested: number; qtyUnit: string }) =>
                l.qtyUnit === 'cylinder' ? count(l.qty ?? 0)
                  : <>{l.qtyRequested} {l.qtyUnit === 'm3' ? 'Nm³' : l.qtyUnit}
                      <div className="muted">{l.qty === null ? 'cylinders settled on loading' : `${count(l.qty)} cylinders`}</div></> },
            { key: 'unit', head: 'Each', right: true, cell: (l: { unitPricePaise: number | null }) => l.unitPricePaise === null ? <Pill tone="danger">no price</Pill> : <Money paise={l.unitPricePaise} /> },
            { key: 'tax', head: 'Tax code', cell: (l: { taxCode: string | null; taxRatePct: number | null }) => l.taxCode ? <>{l.taxCode}{l.taxRatePct !== null ? <span className="muted"> · {l.taxRatePct}%</span> : null}</> : <span className="muted">—</span> },
            { key: 'src', head: 'Priced from', cell: (l: { pricingSource: string | null }) => l.pricingSource ? words(l.pricingSource) : <span className="muted">—</span> },
            { key: 'amount', head: 'Amount', right: true, cell: (l: { amountPaise: number | null }) => <Money paise={l.amountPaise} /> },
          ]}
          rows={d.lines} empty="Nothing is on this offer yet."
          totals={<span>Total <b><Money paise={q.totalPaise} /></b></span>} />
      </Card>

      <div className="grid two">
        <Card title="Commercial terms" sub="what the paper says">
          <div className="detail-grid">
            <div><div className="k">Payment</div><div className="v">{q.paymentDays === null ? 'as agreed' : q.paymentDays === 0 ? 'against delivery' : `${q.paymentDays} days`}</div></div>
            <div><div className="k">GST</div><div className="v">{q.gstExtra === false ? 'Inclusive' : 'Extra'}</div></div>
            <div><div className="k">Delivery</div><div className="v">{q.deliveryTerms ?? 'Ex-Works'}</div></div>
            <div><div className="k">Freight</div><div className="v">{q.freightPaise === null ? 'On actual / as per card' : money(q.freightPaise)}</div></div>
            <div><div className="k">Loading · unloading</div><div className="v">{q.loadingPaisePerCyl === null && q.unloadingPaisePerCyl === null ? 'as per card' : `${money(q.loadingPaisePerCyl ?? 0)} · ${money(q.unloadingPaisePerCyl ?? 0)} per cyl`}</div></div>
            <div><div className="k">P & F</div><div className="v">{q.pnfTerms ?? '—'}</div></div>
            <div><div className="k">Cost-bill after</div><div className="v">{q.costBillDays ? `${q.costBillDays} days` : '—'}</div></div>
            <div><div className="k">Purity certificate</div><div className="v">{q.purityCertificate ?? '—'}</div></div>
            <div><div className="k">Saves to the customer’s card</div><div className="v">{q.applyToCard ? 'Yes, on approval' : 'No'}</div></div>
          </div>
        </Card>
        <Card title="Holding (rental) rates on this offer" sub={q.holdingSlabs?.length ? (q.showHoldingOnPaper ? 'printed on the offer' : 'not printed on the offer') : 'the customer’s card stands'}>
          {q.holdingSlabs?.length ? (
            <Register rows={[{ fromDay: 1, toDay: q.holdingFreeDays ?? 0, ratePaise: -1 }, ...q.holdingSlabs]} empty="" columns={[
              { key: 'd', head: 'Days held', cell: (sl: { fromDay: number; toDay: number }) => sl.toDay >= 100000 ? `Day ${sl.fromDay} onwards` : `Day ${sl.fromDay} to ${sl.toDay}` },
              { key: 'r', head: 'Rate / cyl / day', right: true, cell: (sl: { ratePaise: number }) => sl.ratePaise < 0 ? 'Free' : <Money paise={sl.ratePaise} /> },
            ]} />
          ) : <p className="muted">No holding tiers on this offer.</p>}
        </Card>
      </div>

      {/* 12 Oct — WHAT ELSE THE PAPER CARRIES. The schedules and the terms were
          chosen when the offer was drafted and freeze when it is sent, and
          until now the only way to see them was to print it. */}
      <Card title="What else the paper carries" sub="frozen with the offer — the company's list moving on does not change a sent one">
        <div className="grid two">
          <div>
            {q.printedSchedules.length ? q.printedSchedules.map((sch) => (
              <div key={sch.kind} style={{ marginBottom: 10 }}>
                <div className="k">{SCHEDULE_TITLES[sch.kind] ?? sch.kind}</div>
                <div className="v">{sch.rows.length} row{sch.rows.length === 1 ? '' : 's'} — {sch.rows.slice(0, 3).map((r) => r.item).join(', ')}{sch.rows.length > 3 ? '…' : ''}</div>
              </div>
            )) : <p className="muted">No schedules print under the rates on this offer.</p>}
          </div>
          <div>
            <div className="k">Terms of supply</div>
            <div className="v">{q.offerText.filter((w) => w.kind === 'term').length} numbered clause(s)</div>
            {q.offerText.filter((w) => w.kind !== 'term').map((w, i) => (
              <div key={i} className="muted" style={{ marginTop: 6, fontSize: 12.5 }}>{w.title}</div>
            ))}
            {q.offerText.length === 0 ? <p className="muted">No standing words on this offer — it prints the rates alone.</p> : null}
          </div>
        </div>
      </Card>

      {d.revisions.length > 1 ? (
        <Card title="Every version of this offer" sub="what the customer was told, and when — this is why an issued offer is revised rather than edited">
          <Register
            columns={[
              { key: 'rev', head: 'Revision', cell: (r: { revisionNo: number }) => `rev ${r.revisionNo}` },
              { key: 'number', head: 'Number', cell: (r: { number: string | null }) => r.number ?? <span className="muted">not issued</span> },
              { key: 'issued', head: 'Issued', cell: (r: { issuedOn: string | null }) => date(r.issuedOn) },
              { key: 'total', head: 'Total', right: true, cell: (r: { totalPaise: number }) => <Money paise={r.totalPaise} /> },
              { key: 'status', head: 'Status', cell: (r: { status: string }) => <Pill>{r.status}</Pill> },
            ]}
            rows={d.revisions} empty="none"
            onOpen={(r) => go(`#/quotes/${r.quotationId}`)} />
        </Card>
      ) : null}

      {q.decisionNote || q.revisionReason ? (
        <Card title="What was said">
          {q.revisionReason ? <p><strong>Revised because</strong> — {q.revisionReason}</p> : null}
          {q.decisionNote ? <p style={{ marginBottom: 0 }}><strong>Their answer</strong> — {q.decisionNote}</p> : null}
        </Card>
      ) : null}
    </Page>
  );
}

/**
 * THE LINES OF A DRAFT. Only while it is a draft: once issued, the offer is the
 * paper the customer holds and it is revised, never edited (`quote.setLines`
 * refuses an issued one, and so does the button above).
 */
function EditLines({ quotationId, lines, skus, onDone }: {
  quotationId: number;
  lines: readonly { gasService: string; waterCapacityL: number | null; qty: number | null; note: string | null; skuId: number | null; qtyRequested: number; qtyUnit: string; unitPricePaise?: number | null }[];
  skus: readonly SkuRow[];
  onDone: () => void;
}) {
  const [draft, setDraft] = useState<LineRow[]>(() => lines.length
    ? lines.map((l) => ({ skuId: l.skuId === null ? '' : String(l.skuId), gasService: l.skuId === null ? l.gasService : '',
                          waterCapacityL: l.skuId === null && l.waterCapacityL !== null ? String(l.waterCapacityL) : '',
                          qty: String(l.qtyRequested), unit: (l.qtyUnit === 'm3' || l.qtyUnit === 'kg' ? l.qtyUnit : 'cylinder'),
                          rupees: l.unitPricePaise === null || l.unitPricePaise === undefined ? '' : (l.unitPricePaise / 100).toFixed(2),
                          note: l.note ?? '' }))
    : [emptyLine()]);
  const runner = useRun(MESSAGES);

  async function submit() {
    const ok = await runner.run(() => api.command('quote.setLines', { quotationId, lines: toLineBodies(draft) }));
    if (ok) onDone();
  }

  return (
    <Card title="What is being offered" sub="the whole list is restated — a line left out is a line removed">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <LineEditor rows={draft} skus={skus} onChange={setDraft} idPrefix="e" quotedInGas />
      <Button kind="primary" wide onClick={() => { void submit(); }} disabled={runner.busy || toLineBodies(draft).length === 0}>
        {runner.busy ? 'Saving…' : 'Save these lines'}
      </Button>
    </Card>
  );
}

function IssueQuote({ quotationId, onDone }: { quotationId: number; onDone: () => void }) {
  const [issuedOn, setIssuedOn] = useState(today());
  const [validUntil, setValidUntil] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run((retryKey) => api.command('quote.issue', { quotationId, issuedOn, ...(validUntil ? { validUntil } : {}) }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) onDone();
  }
  return (
    <Card title="Issue this offer" sub="it gets a number, and from then on it is revised rather than edited">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div><Text name="issued-on" label="Issued on" type="date" value={issuedOn} onChange={setIssuedOn} required /></div>
        <div><Text name="valid-until" label="Valid until" type="date" value={validUntil} onChange={setValidUntil} help="after this, an acceptance is refused" /></div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !issuedOn}>{runner.busy ? 'Issuing…' : 'Issue it'}</Button>
    </Card>
  );
}

function ReviseQuote({ quotationId, onDone }: { quotationId: number; onDone: () => void }) {
  const [reason, setReason] = useState(''); const [validUntil, setValidUntil] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(async () => {
      const r = await api.command('quote.revise', { quotationId, reason: reason.trim(), ...(validUntil ? { validUntil } : {}) });
      const v = r.value as { quotationId?: number };
      if (v?.quotationId) go(`#/quotes/${v.quotationId}`);
      return r;
    });
    if (ok) onDone();
  }
  return (
    <Card title="Revise this offer" sub="a new revision, with its own number — this one stays readable">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <TextArea name="revise-reason" label="Why it is being revised" value={reason} onChange={setReason}
                help="the customer will ask what changed; this is the answer" />
      <Text name="revise-valid" label="Valid until" type="date" value={validUntil} onChange={setValidUntil} />
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !reason.trim()}>{runner.busy ? 'Revising…' : 'Make a revision'}</Button>
    </Card>
  );
}

function DecideQuote({ quotationId, onDone }: { quotationId: number; onDone: () => void }) {
  const [decision, setDecision] = useState('accepted');
  const [on, setOn] = useState(today()); const [note, setNote] = useState(''); const [wanted, setWanted] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(async () => {
      const r = await api.command('quote.decide', {
        quotationId, decision: decision === 'declined' ? 'declined' : 'accepted', on,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(decision === 'accepted' && wanted ? { requestedDate: wanted } : {}),
      });
      const v = r.value as { orderId?: number };
      if (v?.orderId) go(`#/orders/${v.orderId}`);
      return r;
    });
    if (ok) onDone();
  }
  return (
    <Card title="Record their answer" sub="an acceptance creates the order at the prices that were offered">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          <Select name="decision" label="What they said" value={decision} onChange={setDecision}
                  options={[{ value: 'accepted', label: 'Accepted it' }, { value: 'declined', label: 'Declined it' }]} />
          <Text name="decided-on" label="On" type="date" value={on} onChange={setOn} required />
        </div>
        <div>
          {decision === 'accepted' ? <Text name="wanted-by" label="Wanted by" type="date" value={wanted} onChange={setWanted} /> : null}
          <TextArea name="decision-note" label="What they said" value={note} onChange={setNote} />
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !on}>
        {runner.busy ? 'Recording…' : decision === 'accepted' ? 'Accepted — make the order' : 'Record the decline'}
      </Button>
    </Card>
  );
}

function NewQuote() {
  // 0065 — a bid is drafted FROM the tender: the tender screen hands over the
  // opportunity and its buyer, and the offer carries the tender from the start (M3-10)
  const tenderFromRoute = routeQuery('tender');
  const tenderId = tenderFromRoute && /^\d+$/.test(tenderFromRoute) ? Number(tenderFromRoute) : null;
  const buyerFromRoute = routeQuery('customer');
  const [customerId, setCustomerId] = useState(buyerFromRoute && /^\d+$/.test(buyerFromRoute) ? buyerFromRoute : ''); const [siteId, setSiteId] = useState('');
  const [reference, setReference] = useState(''); const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  // 0162 — the terms a real KIGL offer is made on (KIGL/QTN/26-27/0008-R1).
  // Pre-filled with what appears on nearly every one, and editable, because
  // the same gas goes out Ex-Works to one buyer and delivered to another.
  // Blank means the offer is SILENT on that charge, not that it quoted zero.
  const [freight, setFreight] = useState(''); const [loadChg, setLoadChg] = useState(''); const [unloadChg, setUnloadChg] = useState('');
  const [paymentDays, setPaymentDays] = useState('30');
  const [deliveryTerms, setDeliveryTerms] = useState('Ex - Works');
  const [gstExtra, setGstExtra] = useState(true);
  const [pnf, setPnf] = useState('EXTRA');
  // 0174 — CTS1's offer: the holding card, when the rates start, whether approval writes the card
  const [freeDays, setFreeDays] = useState('10');
  const [slabs, setSlabs] = useState<{ from: string; to: string; rupees: string }[]>([{ from: '11', to: '30', rupees: '3' }, { from: '31', to: '60', rupees: '4' }, { from: '61', to: '90', rupees: '5' }, { from: '91', to: '', rupees: '10' }]);
  const [showHolding, setShowHolding] = useState(true);
  const [applyToCard, setApplyToCard] = useState(true);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [costBillDays, setCostBillDays] = useState('365');
  const [purity, setPurity] = useState('');
  const [date0] = useState(today());
  // 0175 — the schedules this offer prints. They arrive from the company's own
  // list and are TICKED on; editing one here changes this offer alone.
  const [schedOn, setSchedOn] = useState<Record<string, boolean>>({ security: true, additional: true, service: true, damage: true });
  const [sched, setSched] = useState<Record<string, { item: string; rate: string; hsn: string; gstPct: string }[]>>({});
  const [schedLoaded, setSchedLoaded] = useState(false);
  const [schedOpen, setSchedOpen] = useState<string | null>(null);
  // F04 — the product comes first; the customer is chosen after, and the price is his
  const [lines, setLines] = useState<LineRow[]>([emptyLine()]);
  const runner = useRun(MESSAGES);
  const skus = useAsk(() => api.view('skus.list', { active: true, pageSize: 200 }), [], MESSAGES);
  const gases = useAsk(() => api.view('gases.list', {}), [], MESSAGES);
  const charges = useAsk(() => api.view('charges.list', { active: true }), [], MESSAGES);
  const sites = useAsk(() => (customerId ? api.view('sites.list', { customerId: Number(customerId), active: true }) : Promise.resolve({ rows: [] })), [customerId], MESSAGES);
  // 11 Oct — the first row starts on the first product (CTS1), not on "a gas by name"
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seeded || !skus.data) return;
    setSeeded(true);
    // only an untouched row is seeded — somebody may already be typing
    setLines((cur) => (cur.length === 1 && !cur[0]!.skuId && !cur[0]!.gasService && !cur[0]!.rupees ? [firstLine(skus.data!.rows, true)] : cur));
  }, [skus.data, seeded]);

  useEffect(() => {
    if (schedLoaded || !charges.data) return;
    setSchedLoaded(true);
    const byKind: Record<string, { item: string; rate: string; hsn: string; gstPct: string }[]> = {};
    for (const r of charges.data.rows) {
      (byKind[r.kind] ??= []).push({ item: r.item, rate: r.printedRate, hsn: r.hsn ?? '', gstPct: r.gstPct === null ? '' : String(r.gstPct) });
    }
    setSched(byKind);
  }, [charges.data, schedLoaded]);

  function schedulesBody() {
    return SCHEDULE_KINDS.filter((k) => schedOn[k]).map((kind) => ({
      kind,
      rows: (sched[kind] ?? []).filter((r) => r.item.trim() && r.rate.trim()).map((r) => ({
        item: r.item.trim(), rate: r.rate.trim(),
        ...(r.hsn.trim() ? { hsn: r.hsn.trim() } : {}),
        ...(r.gstPct.trim() ? { gstPct: Number(r.gstPct) } : {}),
      })),
    })).filter((x) => x.rows.length > 0);
  }

  async function submit() {
    const ok = await runner.run(async () => {
      const r = await api.command('quote.draft', {
        customerId: Number(customerId),
        ...(tenderId ? { tenderId } : {}),
        ...(siteId ? { siteId: Number(siteId) } : {}),
        ...(reference.trim() ? { customerReference: reference.trim() } : {}),
        ...(validUntil ? { validUntil } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        lines: toLineBodies(lines),
        ...(rupeesToPaiseOrNull(freight)   != null ? { freightPaise:           rupeesToPaiseOrNull(freight)   as number } : {}),
        ...(rupeesToPaiseOrNull(loadChg)   != null ? { loadingPaisePerCyl:     rupeesToPaiseOrNull(loadChg)   as number } : {}),
        ...(rupeesToPaiseOrNull(unloadChg) != null ? { unloadingPaisePerCyl:   rupeesToPaiseOrNull(unloadChg) as number } : {}),
        ...(paymentDays.trim()   ? { paymentDays: Number(paymentDays) } : {}),
        ...(deliveryTerms.trim() ? { deliveryTerms: deliveryTerms.trim() } : {}),
        gstExtra,
        ...(pnf.trim() ? { pnfTerms: pnf.trim() } : {}),
        holdingFreeDays: Number(freeDays) || 0,
        holdingSlabs: slabs.filter((x) => x.from.trim() && x.rupees.trim()).map((x) => ({ fromDay: Number(x.from), toDay: x.to.trim() ? Number(x.to) : 100000, ratePaise: Math.round(Number(x.rupees) * 100) })),
        showHoldingOnPaper: showHolding, applyToCard,
        ...(effectiveFrom ? { pricesEffectiveFrom: effectiveFrom } : {}),
        ...(costBillDays.trim() ? { costBillDays: Number(costBillDays) } : {}),
        ...(purity.trim() ? { purityCertificate: purity.trim() } : {}),
        printedSchedules: schedulesBody(),
      });
      const v = r.value as { quotationId?: number };
      if (v?.quotationId) go(`#/quotes/${v.quotationId}`);
      return r;
    });
    if (!ok) return;
  }

  const ready = !!customerId && toLineBodies(lines).length > 0;
  const draftBtn = (
    <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !ready}>
      {runner.busy ? 'Drafting…' : 'Draft the quotation'}
    </Button>
  );
  const inp = (idv: string, v: string, set: (x: string) => void, extra: Record<string, unknown> = {}) => (
    <input id={idv} value={v} onChange={(e: { target: { value: string } }) => set(e.target.value)} {...extra} />
  );

  return (
    <Page title={tenderId ? 'Draft the bid' : 'New quotation'} sub="A Techno-Commercial Offer: what is offered, to whom, at what rates and on what terms. The number comes when it is sent."
          actions={draftBtn}>
      <Card>
        {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
        {tenderId ? <Banner kind="info" title={`This offer answers tender #${tenderId}.`} detail="It is priced for the buyer the tender names, and once issued it is what can be recorded as the bid." /> : null}

        <Section n={1} title="Offer" desc="the number is assigned by the system when it is sent">
          <div className="toolbar">
            <div className="field"><label>Offer no.</label><div style={{ padding: '8px 0', fontWeight: 700 }}>QT/… <span className="pill">AUTO</span></div></div>
            <div className="field"><label>Date</label><div style={{ padding: '8px 0' }}>{date(date0)}</div></div>
            <div className="field"><label htmlFor="reference">Their reference (optional)</label>{inp('reference', reference, setReference, { type: 'text', placeholder: 'e.g. their enquiry no.' })}</div>
            <div className="field"><label htmlFor="valid-until">Valid until</label>{inp('valid-until', validUntil, setValidUntil, { type: 'date' })}</div>
            <div className="field"><label htmlFor="q-effective">Prices effective from</label>{inp('q-effective', effectiveFrom, setEffectiveFrom, { type: 'date' })}</div>
          </div>
          <p className="muted" style={{ marginTop: -6 }}>On approval the rates take effect from the date above and stand 30 days unless a validity is set. Blank = the approval date.</p>
        </Section>

        <Section n={2} title="Items" desc="the product first, in the unit it sells in; the rate is ₹ per that unit">
          <LineEditor rows={lines} skus={skus.data?.rows ?? []} gases={gases.data?.rows ?? []} onChange={setLines} idPrefix="q" quotedInGas />
        </Section>

        <Section n={3} title="Buyer" desc="the customer's card pre-fills the rates left blank above">
          <div className="grid two">
            <div>
              <CustomerPickerById name="customer" label="Customer" customerId={customerId}
                                  onPick={(v) => { setCustomerId(v); setSiteId(''); }} placeholder="Choose a customer" />
            </div>
            <div>
              <Select name="site" label="Deliver to (site)" value={siteId} onChange={setSiteId} placeholder={customerId ? 'Any site' : 'Choose the customer first'}
                      options={(sites.data?.rows ?? []).map((s) => ({ value: String(s.siteId), label: s.name + (s.isPrimary ? ' (primary)' : '') }))} />
            </div>
          </div>
        </Section>

        <Section n={4} title="Holding (rental) rates" desc="become the customer's holding card on approval, and print on the offer">
          <div className="toolbar">
            <div className="field"><label htmlFor="q-free">Free days</label>{inp('q-free', freeDays, setFreeDays, { type: 'number', min: 0 })}</div>
            <div className="field"><label>&nbsp;</label><span className="muted">days before rent starts</span></div>
          </div>
          <div className="field"><label>Tiered day-rates (₹ / cylinder / day)</label></div>
          {slabs.map((sl, i) => (
            <div key={i} className="toolbar" style={{ background: 'var(--input)', padding: 10, borderRadius: 8 }}>
              <div className="field"><label htmlFor={`q-slab-from-${i}`}>From day</label>{inp(`q-slab-from-${i}`, sl.from, (v) => setSlabs((o) => o.map((x, k) => k === i ? { ...x, from: v } : x)), { type: 'number' })}</div>
              <div className="field"><label htmlFor={`q-slab-to-${i}`}>To day</label>{inp(`q-slab-to-${i}`, sl.to, (v) => setSlabs((o) => o.map((x, k) => k === i ? { ...x, to: v } : x)), { type: 'number', placeholder: '∞ (open)' })}</div>
              <div className="field"><label htmlFor={`q-slab-rate-${i}`}>₹ / day</label>{inp(`q-slab-rate-${i}`, sl.rupees, (v) => setSlabs((o) => o.map((x, k) => k === i ? { ...x, rupees: v } : x)), { type: 'number' })}</div>
              <Button small kind="danger" onClick={() => setSlabs((o) => o.filter((_, k) => k !== i))}>×</Button>
            </div>
          ))}
          <Button small onClick={() => setSlabs((o) => { const last = o[o.length - 1]; return [...o, { from: last?.to.trim() ? String(Number(last.to) + 1) : '', to: '', rupees: '' }]; })}>+ Add band</Button>
          <p className="muted" style={{ marginTop: 8 }}>Leave the last band’s “to day” blank for an open-ended top tier. One place, no drift: what prints is what bills.</p>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontWeight: 600 }}><input type="checkbox" checked={showHolding} onChange={(e: { target: { checked: boolean } }) => setShowHolding(e.target.checked)} /> Show the holding rate card on the printed offer</label>
        </Section>

        <Section n={5} title="Commercial terms">
          <div className="toolbar">
            <div className="field"><label htmlFor="q-gst">GST</label>
              <select id="q-gst" value={gstExtra ? 'extra' : 'inclusive'} onChange={(e: { target: { value: string } }) => setGstExtra(e.target.value === 'extra')}>
                <option value="extra">Extra — charged on top of these rates</option>
                <option value="inclusive">Inclusive — these rates already carry it</option>
              </select></div>
            <div className="field"><label htmlFor="q-pnf">P &amp; F</label>{inp('q-pnf', pnf, setPnf, { type: 'text', placeholder: 'EXTRA' })}</div>
            <div className="field"><label htmlFor="q-delivery">Delivery condition</label>{inp('q-delivery', deliveryTerms, setDeliveryTerms, { type: 'text', placeholder: 'Ex - Works' })}</div>
            <div className="field"><label htmlFor="q-payment">Payment terms (days)</label>{inp('q-payment', paymentDays, setPaymentDays, { type: 'number', min: 0 })}</div>
          </div>
          <div className="toolbar">
            <div className="field"><label htmlFor="q-freight">Freight (₹ per trip)</label>{inp('q-freight', freight, setFreight, { type: 'text', placeholder: 'not quoted' })}</div>
            <div className="field"><label htmlFor="q-loading">Loading (₹ per cylinder)</label>{inp('q-loading', loadChg, setLoadChg, { type: 'text', placeholder: 'not quoted' })}</div>
            <div className="field"><label htmlFor="q-unloading">Unloading (₹ per cylinder)</label>{inp('q-unloading', unloadChg, setUnloadChg, { type: 'text', placeholder: 'not quoted' })}</div>
            <div className="field"><label htmlFor="q-costbill">Cost-bill after (days)</label>{inp('q-costbill', costBillDays, setCostBillDays, { type: 'number', min: 0 })}
              <div className="help">Non-return billed at replacement cost after this many days.</div></div>
            <div className="field"><label htmlFor="q-purity">Purity certificate</label>{inp('q-purity', purity, setPurity, { type: 'text', placeholder: 'e.g. with every lot' })}</div>
          </div>
          <p className="muted">Freight is one amount for the trip, charged only when our own truck delivers. Loading is at our plant, unloading at the customer’s gate. Left blank, the offer is silent on a charge and billing keeps reading the customer’s card.</p>
        </Section>

        <Section n={6} title="Printed schedules" desc="tick what prints under the rates; edit a value and it changes THIS offer only">
          {SCHEDULE_KINDS.map((kind) => {
            const rows = sched[kind] ?? [];
            const open = schedOpen === kind;
            return (
              <div key={kind} className="sched">
                <div className="sched-head">
                  <input type="checkbox" checked={!!schedOn[kind]} id={`q-sched-${kind}`}
                         onChange={(e: { target: { checked: boolean } }) => setSchedOn((o) => ({ ...o, [kind]: e.target.checked }))} />
                  <label htmlFor={`q-sched-${kind}`}><b>{SCHEDULE_TITLES[kind]}</b></label>
                  <span className="muted">{rows.length ? `${rows.length} row${rows.length === 1 ? '' : 's'} · ${rows.slice(0, 3).map((r) => r.item).join(', ')}${rows.length > 3 ? '…' : ''}` : 'nothing on it'}</span>
                  <Button small onClick={() => setSchedOpen(open ? null : kind)}>{open ? 'Done' : 'Edit'}</Button>
                </div>
                {open ? (
                  <>
                    <table className="register sched-table">
                      <thead><tr><th>Item</th><th>Rate, as it prints</th><th>HSN / SAC</th><th>GST %</th><th /></tr></thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={i}>
                            <td><input value={r.item} aria-label="Item" onChange={(e: { target: { value: string } }) => setSched((o) => ({ ...o, [kind]: (o[kind] ?? []).map((x, k) => k === i ? { ...x, item: e.target.value } : x) }))} /></td>
                            <td><input value={r.rate} aria-label="Rate" placeholder="Rs 900/ Pc + GST" onChange={(e: { target: { value: string } }) => setSched((o) => ({ ...o, [kind]: (o[kind] ?? []).map((x, k) => k === i ? { ...x, rate: e.target.value } : x) }))} /></td>
                            <td><input value={r.hsn} aria-label="HSN or SAC" onChange={(e: { target: { value: string } }) => setSched((o) => ({ ...o, [kind]: (o[kind] ?? []).map((x, k) => k === i ? { ...x, hsn: e.target.value } : x) }))} /></td>
                            <td><input value={r.gstPct} aria-label="GST per cent" type="number" onChange={(e: { target: { value: string } }) => setSched((o) => ({ ...o, [kind]: (o[kind] ?? []).map((x, k) => k === i ? { ...x, gstPct: e.target.value } : x) }))} /></td>
                            <td><Button small kind="danger" onClick={() => setSched((o) => ({ ...o, [kind]: (o[kind] ?? []).filter((_, k) => k !== i) }))}>×</Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <Button small onClick={() => setSched((o) => ({ ...o, [kind]: [...(o[kind] ?? []), { item: '', rate: '', hsn: '', gstPct: '' }] }))}>+ Add a row</Button>
                  </>
                ) : null}
              </div>
            );
          })}
          <p className="muted">These start from the company’s own schedules (Settings → Products → Charges). What this offer prints is frozen when it is sent, so changing the company list later never rewrites an offer already out.</p>
        </Section>

        <Section n={7} title="Notes and approval">
          <TextArea name="notes" label="Notes on the offer" value={notes} onChange={setNotes} />
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '0 0 14px', fontWeight: 600 }}><input type="checkbox" checked={applyToCard} onChange={(e: { target: { checked: boolean } }) => setApplyToCard(e.target.checked)} /> Save these rates to the customer’s card when approved — they then govern every dispatch and bill</label>
          {draftBtn}
          <p className="muted" style={{ marginBottom: 0 }}>Drafting does not send it. Next: submit it for approval, then issue it as the Techno-Commercial Offer.</p>
        </Section>
      </Card>
    </Page>
  );
}
